import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import TurkishMosaicWall from '../TurkishMosaicWall';
import { APP_LINKS } from '../../constants/appLinks';
import { GlobeIcon } from '../Icons';
import appIcon from '../../assets/app/app-icon.png';
import appStoreBadgeEn from '../../assets/app/app-store-badge-en.svg';
import appStoreBadgeTr from '../../assets/app/app-store-badge-tr.svg';
import googlePlayBadgeEn from '../../assets/app/google-play-badge-en.png';
import googlePlayBadgeTr from '../../assets/app/google-play-badge-tr.png';

// Hero görselleri
import hero1 from '../../assets/hero/hero_1.jpg';
import hero2 from '../../assets/hero/hero_2.jpg';
import hero3 from '../../assets/hero/hero3.jpg';
import hero4 from '../../assets/hero/hero4.jpg';
import hero5 from '../../assets/hero/hero5.jpg';
import hero6 from '../../assets/hero/hero6.jpg';

const Hero = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [hero1, hero2, hero3, hero4, hero5, hero6];

  // Otomatik görsel değişimi
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // 5 saniyede bir değişir

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const memberFlags = [
    { name: 'Türkiye', flag: FLAGS.turkey },
    { name: language === 'tr' ? 'Azerbaycan' : 'Azerbaijan', flag: FLAGS.azerbaijan },
    { name: language === 'tr' ? 'Kazakistan' : 'Kazakhstan', flag: FLAGS.kazakhstan },
    { name: language === 'tr' ? 'Kırgızistan' : 'Kyrgyzstan', flag: FLAGS.kyrgyzstan },
    { name: language === 'tr' ? 'Özbekistan' : 'Uzbekistan', flag: FLAGS.uzbekistan },
  ];

  const gradientOverlay = isDarkMode
    ? `linear-gradient(180deg, 
        rgba(13, 13, 26, 0.85) 0%, 
        rgba(26, 26, 46, 0.8) 30%, 
        rgba(45, 45, 68, 0.85) 60%,
        rgba(13, 13, 26, 0.95) 100%)`
    : `linear-gradient(180deg, 
        rgba(0, 171, 194, 0.85) 0%, 
        rgba(0, 138, 156, 0.75) 15%, 
        rgba(0, 107, 122, 0.8) 30%,
        rgba(26, 26, 46, 0.9) 60%,
        rgba(26, 26, 46, 0.98) 100%)`;

  return (
    <section id="home" style={styles.hero}>
      {/* Arka Plan Görselleri */}
      <div style={styles.backgroundContainer}>
        {heroImages.map((image, index) => (
          <div
            key={index}
            style={{
              ...styles.backgroundImage,
              backgroundImage: `url(${image})`,
              opacity: currentImageIndex === index ? 1 : 0,
            }}
          />
        ))}
      </div>

      {/* Gradient Overlay */}
      <div style={{
        ...styles.gradientOverlay,
        background: gradientOverlay,
      }} />

      {/* Motif Pattern Overlay - Güncellendi */}
      <div style={styles.patternOverlay}>
        <TurkishMosaicWall />
      </div>

      {/* İçerik */}
      <div style={styles.content}>
        <div style={{
          ...styles.badge,
          backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : 'rgba(255, 255, 255, 0.15)',
          borderColor: isDarkMode ? DARK_THEME.primary : 'rgba(255, 255, 255, 0.3)',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
            <GlobeIcon size={16} strokeWidth={2.2} />
            {language === 'tr' ? 'Türk Devletlerini Bir Araya Getiriyoruz' : 'Uniting Turkic Nations'}
          </span>
        </div>
        <h1 style={styles.title}>
          {language === 'tr' ? 'Modern' : 'The Modern'} <span style={{...styles.highlight, color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary}}>{language === 'tr' ? 'İpek Yolu' : 'Silk Road'}</span>
        </h1>
        <p style={styles.subtitle}>
          {language === 'tr'
            ? "Azerbaycan, Kazakistan, Kırgızistan, Türkiye ve Özbekistan'ı ortak miras, kültür ve ekonomik iş birliği yoluyla birbirine bağlıyoruz"
            : 'Connecting Azerbaijan, Kazakhstan, Kyrgyzstan, Türkiye & Uzbekistan through shared heritage, culture, and economic cooperation'}
        </p>
        
        {/* Member Flags */}
        <div style={styles.flagsContainer}>
          {memberFlags.map((item, index) => (
            <div key={index} style={styles.flagItem}>
              <img src={item.flag} alt={item.name} style={styles.flagImage} />
              <span style={styles.flagName}>{item.name}</span>
            </div>
          ))}
        </div>

        <div style={{
          ...styles.stats,
          backgroundColor: isDarkMode ? `${DARK_THEME.surface}90` : 'rgba(255, 255, 255, 0.1)',
          borderColor: isDarkMode ? DARK_THEME.border : 'rgba(255, 255, 255, 0.15)',
        }}>
          <div style={styles.statItem}>
            <span style={{...styles.statNumber, color: isDarkMode ? DARK_THEME.primary : COLORS.secondary}}>5+</span>
            <span style={styles.statLabel}>{language === 'tr' ? 'Üye Devlet' : 'Member States'}</span>
          </div>
          <div style={styles.statDivider}></div>
          <div style={styles.statItem}>
            <span style={{...styles.statNumber, color: isDarkMode ? DARK_THEME.primary : COLORS.secondary}}>170M+</span>
            <span style={styles.statLabel}>{language === 'tr' ? 'Nüfus' : 'Population'}</span>
          </div>
          <div style={styles.statDivider}></div>
          <div style={styles.statItem}>
            <span style={{...styles.statNumber, color: isDarkMode ? DARK_THEME.primary : COLORS.secondary}}>4.5M</span>
            <span style={styles.statLabel}>{language === 'tr' ? 'km² Yüzölçümü' : 'km² Territory'}</span>
          </div>
        </div>
        <div style={styles.buttons}>
          <Link to="/about" style={{
            ...styles.primaryButton,
            backgroundColor: isDarkMode ? DARK_THEME.primary : COLORS.secondary,
            color: isDarkMode ? COLORS.dark : COLORS.dark,
          }}>{language === 'tr' ? "TDT'yi Keşfedin" : 'Explore OTS'}</Link>
          <Link to="/member-states" style={{
            ...styles.secondaryButton,
            borderColor: isDarkMode ? DARK_THEME.primary : 'rgba(255, 255, 255, 0.5)',
            color: isDarkMode ? DARK_THEME.primary : COLORS.white,
          }}>{language === 'tr' ? 'Üye Devletler' : 'Member States'}</Link>
        </div>

        {/* Mobil Uygulama - Mağaza Bağlantıları */}
        <div style={{
          ...styles.appRow,
          backgroundColor: isDarkMode ? `${DARK_THEME.surface}90` : 'rgba(255, 255, 255, 0.1)',
          borderColor: isDarkMode ? DARK_THEME.border : 'rgba(255, 255, 255, 0.15)',
        }}>
          <img src={appIcon} alt="Turkic Silk Road App" style={styles.appRowIcon} />
          <div style={styles.appRowTexts}>
            <span style={styles.appRowName}>Turkic Silk Road</span>
            <span style={styles.appRowTagline}>
              {language === 'tr' ? 'Mobil uygulamamızı ücretsiz indirin' : 'Download our free mobile app'}
            </span>
          </div>
          <div style={styles.appRowBadges}>
            <a
              href={APP_LINKS.appStore}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="App Store"
            >
              <img
                src={language === 'tr' ? appStoreBadgeTr : appStoreBadgeEn}
                alt={language === 'tr' ? "App Store'dan İndirin" : 'Download on the App Store'}
                style={styles.appRowStoreBadge}
              />
            </a>
            <a
              href={APP_LINKS.googlePlay}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Google Play"
            >
              <img
                src={language === 'tr' ? googlePlayBadgeTr : googlePlayBadgeEn}
                alt={language === 'tr' ? "Google Play'den alın" : 'Get it on Google Play'}
                style={styles.appRowGoogleBadge}
              />
            </a>
          </div>
        </div>
      </div>

      {/* Görsel İndikatörleri - Sağda dikey */}
      <div style={styles.imageIndicators}>
        {heroImages.map((_, index) => (
          <button
            key={index}
            style={{
              ...styles.indicator,
              backgroundColor: currentImageIndex === index 
                ? (isDarkMode ? DARK_THEME.primary : COLORS.white) 
                : 'rgba(255, 255, 255, 0.4)',
              transform: currentImageIndex === index ? 'scale(1.3)' : 'scale(1)',
              width: currentImageIndex === index ? '12px' : '10px',
              height: currentImageIndex === index ? '12px' : '10px',
            }}
            onClick={() => setCurrentImageIndex(index)}
            aria-label={language === 'tr' ? `${index + 1}. görsele git` : `Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div style={styles.scrollIndicator}>
        <span style={styles.scrollText}>{language === 'tr' ? 'Keşfetmek için kaydırın' : 'Scroll to explore'}</span>
        <div style={styles.scrollArrow}>↓</div>
      </div>
    </section>
  );
};

const styles = {
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
    padding: '120px 20px 80px',
    overflow: 'hidden',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transition: 'opacity 1.5s ease-in-out',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  patternOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundRepeat: 'repeat',
    zIndex: 2,
    opacity: 1,
  },
  content: {
    position: 'relative',
    zIndex: 3,
    maxWidth: '900px',
  },
  badge: {
    display: 'inline-block',
    border: '1px solid',
    padding: '10px 25px',
    borderRadius: '50px',
    marginBottom: '30px',
    color: COLORS.white,
    fontSize: '14px',
    fontWeight: '500',
    backdropFilter: 'blur(10px)',
  },
  title: {
    fontSize: 'clamp(40px, 8vw, 72px)',
    color: COLORS.white,
    fontWeight: '700',
    lineHeight: '1.1',
    marginBottom: '25px',
    textShadow: '0 2px 20px rgba(0,0,0,0.3)',
  },
  highlight: {
    display: 'block',
  },
  subtitle: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.9)',
    lineHeight: '1.7',
    maxWidth: '650px',
    margin: '0 auto 30px',
    textShadow: '0 1px 10px rgba(0,0,0,0.2)',
  },
  flagsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '25px',
    marginBottom: '35px',
    flexWrap: 'wrap',
  },
  flagItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  flagImage: {
    height: '40px',
    width: 'auto',
    borderRadius: '4px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    transition: 'transform 0.3s ease',
  },
  flagName: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '500',
  },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '30px',
    marginBottom: '40px',
    flexWrap: 'wrap',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '25px 40px',
    border: '1px solid',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: '700',
  },
  statLabel: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.8)',
    marginTop: '5px',
  },
  statDivider: {
    width: '1px',
    height: '40px',
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  buttons: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    padding: '16px 40px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'inline-block',
  },
  secondaryButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    padding: '16px 40px',
    fontSize: '16px',
    fontWeight: '600',
    border: '2px solid',
    borderRadius: '50px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    backdropFilter: 'blur(10px)',
  },
  appRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '18px',
    marginTop: '35px',
    padding: '14px 26px',
    borderRadius: '20px',
    border: '1px solid',
    backdropFilter: 'blur(10px)',
  },
  appRowIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '11px',
    boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  },
  appRowTexts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
  },
  appRowName: {
    fontSize: '16px',
    fontWeight: '700',
    color: COLORS.white,
  },
  appRowTagline: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.75)',
    marginTop: '2px',
  },
  appRowBadges: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  appRowStoreBadge: {
    height: '40px',
    display: 'block',
  },
  // Google Play PNG rozetinde kenar boşluğu gömülü olduğundan daha yüksek tutulur
  appRowGoogleBadge: {
    height: '60px',
    display: 'block',
    margin: '-10px 0',
  },
  imageIndicators: {
    position: 'absolute',
    right: '30px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    zIndex: 4,
  },
  indicator: {
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: 0,
    backgroundColor: 'transparent',
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.6)',
    zIndex: 4,
  },
  scrollText: {
    fontSize: '12px',
    marginBottom: '10px',
  },
};

export default Hero;
